import { useEffect, useRef } from "react";
import styles from "./HoverWord.module.scss";
import { FigTag } from "./FigTag";
import { BIG_H, lerpK, useWordBox, type OverlayProps } from "./shared";

const HANDLE = 8;

/* Figma quotation, matched to the real canvas chrome: selection bounds
   hugging the object (Figma pads nothing), 8px white corner handles with
   blue borders, the frame-name label in Inter 11 above-left (text only —
   plain frames carry no icon), the W × H badge in Inter 11 on blue below
   center, and the visitor's pointer as the multiplayer cursor (the
   canonical 24x36 arrow path, white outline) with an Inter name tag.
   Small instances (statement text) keep bounds + handles + cursor and
   drop the label and badge, which need air the line box doesn't have. */
export function DesignOverlay({
  hostRef,
  posRef,
  hot,
  text,
  tap,
}: OverlayProps) {
  const box = useWordBox(hostRef);
  const cursorRef = useRef<HTMLDivElement>(null);

  /* The shell tracks the pointer from before this overlay existed, so the
     cursor starts exactly at the entry point. The lerp lag is on purpose —
     multiplayer cursors trail the hand. On touch there is no hand to
     trail, so the collaborator ARRIVES: it starts just off the word's
     lower right and glides on a gentle arc (x and y settle at different
     rates, which bends the straight line) to rest high on the frame's
     right side, where its name tag stays clear of the centred W × H
     badge. The loop keeps
     running through the exit so the cursor never freezes mid-glide. box
     gates the first real render, so the effect re-arms on it once the
     cursor node exists. */
  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor || !box || !posRef.current.seeded) return;
    const target = tap
      ? { x: box.w * 0.74, y: box.h * 0.26 }
      : posRef.current;
    let raf = 0;
    let last = performance.now();
    let x = posRef.current.x;
    let y = posRef.current.y;
    const [kx, ky] = tap ? [0.1, 0.075] : [0.3, 0.3];
    if (tap) {
      x = box.w + 28;
      y = box.h + 22;
    }
    cursor.dataset.live = "1";

    const loop = (now: number) => {
      const dt = Math.min(64, now - last);
      last = now;
      x += (target.x - x) * lerpK(kx, dt);
      y += (target.y - y) * lerpK(ky, dt);
      cursor.style.transform = `translate(${x}px, ${y}px)`;
      raf = requestAnimationFrame(loop);
    };
    cursor.style.transform = `translate(${x}px, ${y}px)`;
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [posRef, tap, box]);

  if (!box) return null;
  const { w, h } = box;
  const big = h >= BIG_H;
  const bx = 2.5; /* the measured box's side bearings already ≈ Figma's hug */
  const selX = -bx;
  const selY = h * 0.08;
  const selW = w + bx * 2;
  const selH = h * 0.96;
  const dims = `${Math.round(selW)} × ${Math.round(selH)}`;
  const pillW = dims.length * 6.4 + 13;
  const pillX = selX + selW / 2 - pillW / 2;
  const pillY = selY + selH + 5; /* tucked — line 2 sits close below */
  const pillH = 18;
  const corners: Array<[number, number]> = [
    [selX, selY],
    [selX + selW, selY],
    [selX, selY + selH],
    [selX + selW, selY + selH],
  ];

  return (
    <span className={styles.overlay} data-out={hot ? "0" : "1"} aria-hidden>
      <svg className={styles.svg} viewBox={`0 0 ${w} ${h}`}>
        <rect
          className={styles.figBounds}
          x={selX}
          y={selY}
          width={selW}
          height={selH}
        />
        {corners.map(([cx, cy], i) => (
          <rect
            key={i}
            className={styles.figHandle}
            x={cx - HANDLE / 2}
            y={cy - HANDLE / 2}
            width={HANDLE}
            height={HANDLE}
            style={{ "--i": i } as React.CSSProperties}
          />
        ))}
        {big && (
          <text className={styles.figLabel} x={selX} y={selY - 8}>
            {text}
          </text>
        )}
        {big && (
          <g className={styles.figDims}>
            <rect
              className={styles.figDimsBg}
              x={pillX}
              y={pillY}
              width={pillW}
              height={pillH}
              rx={2}
            />
            <text
              className={styles.figDimsText}
              x={pillX + pillW / 2}
              y={pillY + 13}
              textAnchor="middle"
            >
              {dims}
            </text>
          </g>
        )}
      </svg>
      <div ref={cursorRef} className={styles.figCursor}>
        <svg width="24" height="36" viewBox="0 0 24 36" fill="none">
          <path
            d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19841L11.7841 12.3673H5.65376Z"
            fill="var(--fig)"
            stroke="#fff"
            strokeWidth="1"
          />
        </svg>
        <FigTag className={styles.figTag} />
      </div>
    </span>
  );
}
